import React from "react";
import { useSelector } from "react-redux";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import {
  DynamicObjectListField,
  FieldHeader,
  SectionTitle,
  SeparatorBar,
  TextField,
} from "../../../components";
import * as commonStyles from "../../../components/commonStyles/commonStyles.module.css";
import { SubForm, SubFormNavigationProps } from "../../../components/SubForm";
import {
  gameInfoSupplementaryDataInitialValues,
  useAaaGameInformationSupplementaryDataSchema,
} from "../../../helpers/formValidation";
import { useStrings } from "../../../localization/localizationHelpers";
import { AaaGameInformationConceptDetailsDataType } from "../../../models/forms";
import { OnboardingRootState } from "../../../redux/reducers/rootReducer";
import { getIsSavingForm, getSubFormData } from "../../../redux/selectors";
import {
  AaaGameInformationFormId,
  AaaGameInformationSubFormsId,
} from "../AaaGameInformationPageConstants";
import * as styles from "./SupplementaryDataSubForm.module.css";

const SupplementaryDataSubForm = (props: SubFormNavigationProps) => {
  const [
    developmentStudioExperience,
    listTheMostRecentTitleCredits,
    titlesPreviouslyShippedByYourStudio,
    titlesPreviouslyShippedByYourTeamMembers,
    competitorTitles,
    competitorTitlesSubtitle,
    platform,
    gameTitle,
    yearShipped,
    teamMemberName,
    addTitle,
    addCompetitorTitle,
    gameDeveloper,
  ] = useStrings(
    "developmentStudioExperience",
    "listTheMostRecentTitleCredits",
    "titlesPreviouslyShippedByYourStudio",
    "titlesPreviouslyShippedByYourTeamMembers",
    "competitorTitles",
    "competitorTitlesSubtitle",
    "platform",
    "gameTitle",
    "yearShipped",
    "teamMemberName",
    "addTitle",
    "addCompetitorTitle",
    "gameDeveloper"
  );
  const validationSchema = useAaaGameInformationSupplementaryDataSchema();
  const isSavingFormToCrm = useSelector(getIsSavingForm);
  const conceptDetailsSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.ConceptDetails)
  ) as AaaGameInformationConceptDetailsDataType;

  return (
    <SubForm
      validateConfig={validationSchema}
      initialValues={gameInfoSupplementaryDataInitialValues}
      formId={AaaGameInformationFormId}
      subFormId={AaaGameInformationSubFormsId.SupplementaryData}
      {...props}
    >
      <SectionTitle title={developmentStudioExperience} subtitle={listTheMostRecentTitleCredits} />
      {conceptDetailsSubFormData?.gameDevelopers && (
        <Row className={commonStyles.container}>
          <Column className={styles.gameDevelopersColumn}>
            <FieldHeader title={gameDeveloper} titleClassName={commonStyles.textFieldLabel} />
            {conceptDetailsSubFormData.gameDevelopers.map((developer, index) => (
              <span key={`developer-${index}`}>{developer}</span>
            ))}
          </Column>
        </Row>
      )}
      <div className={styles.dynamicContainer}>
        <DynamicObjectListField
          hasLabels={true}
          name="devStudioExperience"
          title={titlesPreviouslyShippedByYourStudio}
          buttonLabel={addTitle}
          disabled={isSavingFormToCrm}
          renderChild={(name: string, index: number) => (
            <Row className={styles.contentRow}>
              <Column className={styles.field}>
                <TextField
                  name={`${name}.${index}.platform`}
                  title={platform}
                  isSaving={isSavingFormToCrm}
                  onlyInternalElements={true}
                />
              </Column>
              <Column className={styles.field}>
                <TextField
                  name={`${name}.${index}.gameTitle`}
                  title={gameTitle}
                  isSaving={isSavingFormToCrm}
                  onlyInternalElements={true}
                />
              </Column>
              <Column className={styles.field}>
                <TextField
                  name={`${name}.${index}.yearShipped`}
                  title={yearShipped}
                  isSaving={isSavingFormToCrm}
                  onlyInternalElements={true}
                />
              </Column>
            </Row>
          )}
        />
        <SeparatorBar />
        <DynamicObjectListField
          hasLabels={true}
          name="teamMembersPreviousTitles"
          title={titlesPreviouslyShippedByYourTeamMembers}
          buttonLabel={addTitle}
          disabled={isSavingFormToCrm}
          renderChild={(name: string, index: number) => (
            <Row className={styles.contentRow}>
              <Column>
                <Row>
                  <Column className={styles.field}>
                    <TextField
                      name={`${name}.${index}.memberName`}
                      title={teamMemberName}
                      isSaving={isSavingFormToCrm}
                      onlyInternalElements={true}
                    />
                  </Column>
                  <Column className={styles.field}>
                    <TextField
                      name={`${name}.${index}.gameTitle`}
                      title={gameTitle}
                      isSaving={isSavingFormToCrm}
                      onlyInternalElements={true}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column className={styles.field}>
                    <TextField
                      name={`${name}.${index}.platform`}
                      title={platform}
                      isSaving={isSavingFormToCrm}
                      onlyInternalElements={true}
                    />
                  </Column>
                  <Column className={styles.field}>
                    <TextField
                      name={`${name}.${index}.yearShipped`}
                      title={yearShipped}
                      isSaving={isSavingFormToCrm}
                      onlyInternalElements={true}
                    />
                  </Column>
                </Row>
              </Column>
            </Row>
          )}
        />
        <SeparatorBar />
        <DynamicObjectListField
          hasLabels={true}
          name="competitorTitles"
          title={competitorTitles}
          subtitle={competitorTitlesSubtitle}
          buttonLabel={addCompetitorTitle}
          disabled={isSavingFormToCrm}
          renderChild={(name: string, index: number) => (
            <Row className={styles.contentRow}>
              <Column className={styles.field}>
                <TextField
                  name={`${name}.${index}.platform`}
                  title={platform}
                  isSaving={isSavingFormToCrm}
                  onlyInternalElements={true}
                />
              </Column>
              <Column className={styles.field}>
                <TextField
                  name={`${name}.${index}.gameTitle`}
                  title={gameTitle}
                  isSaving={isSavingFormToCrm}
                  onlyInternalElements={true}
                />
              </Column>
            </Row>
          )}
        />
      </div>
    </SubForm>
  );
};

export default SupplementaryDataSubForm;
